import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	IconButton,
	InputAdornment,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography,
	Grid,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import React, {
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/Help';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import Swal from 'sweetalert2';
import LoopIcon from '@mui/icons-material/Loop';
import HelpForm from '../../views/forms/HelpForm';
import AsyncAutocomplete from '../common/AsyncAutocomplete';
import LoadingDialog from '../common/LoadingDialog';
import Confirm from '../confirm';
import { deleteRecord, saveRecord } from '../crud-helper';
import consts from '../../consts';
import ModelFormProvider from '../../contexts/ModelForm';
import { usePrerequisites } from '../../contexts/Prerequisites';
import { forwardEnsuredRef, useDeepMemo, useDidMount, useDidUpdate, useLiveRef, useUniqueEffect } from '../../hooks';
import util from '../../util/index';
import DateTimePickerActionBar from '../common/DateTimePickerActionBar';
import AccordionFormGroups from './AccordionFormGroups';
import { withInputWrapper } from './InputWrapper';
import SectionFormGroups from './SectionFormGroups';
import TabFormGroups from './TabFormGroups';

const printForm = () => {
	window.print();
};

const formGroupsComponents = new Map([
	['accordions', AccordionFormGroups],
	['tabs', TabFormGroups],
	['sections', SectionFormGroups]
]);

export class FieldMappers {
	boolean = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			readOnly,
			required,
			error,
			disabled,
			onChange
		}) => {
			return (
				<Box>
					{inlineLabel ? (
						<Checkbox
							{...otherProps}
							name={field}
							disabled={disabled}
							checked={formik.values[field]}
							onChange={!readOnly ? onChange : undefined}
						/>
					) : (
						<FormControlLabel
							checked={formik.values[field]}
							sx={{
								color: 'primary.light'
							}}
							control={
								<Checkbox
									{...otherProps}
									name={field}
									disabled={disabled}
									checked={formik.values[field]}
									onChange={!readOnly ? onChange : undefined}
									onBlur={formik.handleBlur}
								/>
							}
							required={required}
							label={fieldLabel}
						/>
					)}
				</Box>
			);
		}
	);
	file = withInputWrapper(({ column, field, fieldLabel, formik, activeRecord, otherProps, readOnly, error, onChange }) => {
		const previewRef = useRef();
		const ref = useRef();
		const helperText = error || column.helperText;
		const [loading, setLoading] = useState(false);
		const [{ strings }] = usePrerequisites();
		const [fileName, setFileName] = useState(formik.values[field]?.name);
		const [preview, setPreview] = useState(
			typeof formik.values[field] === 'string' &&
				!/^@[a-zA-Z0-9+/]+={,2}$/.test(formik.values[field]) &&
				`${field}Url` in formik.values
				? formik.values[`${field}Url`]
				: formik.values[field]
		);
		const imageTypes = ['image/gif', 'image/jpeg', 'image/png'];
		const isImage = column.accept?.split(',')?.some((accepts) => imageTypes.includes(accepts.trim()));

		const fieldValue = formik.values[field];

		const loadPreview = useCallback(() => {
			if (formik.values[field] && isImage) {
				if (fieldValue instanceof File) {
					setLoading(true);
					let reader = new FileReader();
					reader.onloadend = () => {
						setLoading(false);
						setPreview(reader.result);
					};
					reader.readAsDataURL(formik.values[field]);
				} else {
					setLoading(false);
					const previewVal =
						formik?.values &&
							typeof formik.values[field] === 'string' &&
							!/^@[a-zA-Z0-9+/]+={,2}$/.test(formik.values[field]) &&
							`${field}Url` in formik.values
							? column.fileLocation ? typeof column.fileLocation === "function" ? `${column.fileLocation({ formik, activeRecord })}/${formik.values[`${field}Url`]}` : `${column.fileLocation}/${formik.values[`${field}Url`]}` : formik.values[`${field}Url`]
							: column.fileLocation ? `${column.fileLocation}/${formik.values[field]}` : formik.values[field];
					setPreview(previewVal);
				}
			}
		}, [formik.values[field]]);

		const handleChange = (event) => {
			event.persist();
			const file = event.target.files[0];

			setFileName(file?.name);
			const reader = new FileReader();
			if ((column.readAsDataURL || column.readAsText) && file instanceof File) {
				reader.onloadend = () => {
					onChange(reader.result, true, true);
					setTimeout(() => formik.setFieldTouched(field, true), 50);
				};
				if (column.readAsDataURL) {
					reader.readAsDataURL(file);
				} else if (column.readAsText) {
					reader.readAsText(file);
				}
			} else {
				onChange(file);
				setPreview(file, true, true);
				setTimeout(() => formik.setFieldTouched(field, true), 50);
			}
			event.target.value = null; //Clear value to allow same file reselection
		};

		useEffect(() => {
			loadPreview();
		}, [fieldValue]);

		return (
			<Grid container>
				{isImage && preview && (
					<Grid size={12}>
						<Box
							component="img"
							src={preview}
							alt="preview image"
							sx={{ width: (theme) => theme.spacing(10) }}
							ref={previewRef}
						/>
					</Grid>
				)}
				<Grid size={12}>
					<Stack direction="row" alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
						<Button
							type="button"
							variant="outlined"
							size="small"
							component="label"
							color={error ? 'error' : 'primary'}
							disabled={readOnly}
						>
							{column.ChooseFileTxt
								? strings[column.ChooseFileTxt] || column.ChooseFileTxt
								: strings.ChooseFile || 'Choose File'}
							<input
								hidden
								{...otherProps}
								accept={column.accept}
								onChange={handleChange}
								name={field}
								type="file"
								ref={ref}
							/>
						</Button>
						{!column.hideFileName && ((!column.readAsDataURL && !column.readAsText) || !!fileName) && (
							<Typography
								component="span"
								color={error ? 'error' : 'textPrimary'}
								sx={{
									overflow: 'hidden',
									whiteSpace: 'pre-wrap' /* css-3 */,
									wordWrap: 'break-word' /* Internet Explorer 5.5+ */
								}}
							>
								{fileName || formik.values[field]?.name || fieldValue}
							</Typography>
						)}
					</Stack>
					{(!!helperText || !!error) && (
						<FormHelperText component="span" error={!!error}>
							{error || helperText}
						</FormHelperText>
					)}
				</Grid>
			</Grid>
		);
	});
	select = withInputWrapper(
		({ column, field, fieldLabel, formik, activeRecord, otherProps, inlineLabel = true, readOnly }) => {
			const [{ strings }] = usePrerequisites();
			let options = typeof column.lookup === 'string' ? activeRecord?.combos[column.lookup] : column.lookup;
			if (!Array.isArray(options)) {
				options = [];
			}
			const multiple = column.multiple === true;

			return (
				<Select
					size="small"
					margin="dense"
					variant="outlined"
					{...otherProps}
					name={field}
					label={inlineLabel ? '' : fieldLabel}
					multiple={multiple}
					readOnly={readOnly}
					value={formik.values[field]}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					sx={{
						...otherProps.sx,
						flex: 1
					}}
					renderValue={
						multiple
							? (selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((value) => {
										const option = options.find((o) => o.LookupId === value) || {
											DisplayValue: strings[value.replace(/\s+/g, '')] || value
										};
										return <Chip key={value} label={option.DisplayValue} />;
									})}
								</Box>
							)
							: undefined
					}
					endAdornment={
						column.addable && (
							<InputAdornment position="end" sx={{ marginRight: (theme) => theme.spacing(2) }}>
								<IconButton color="primary" sx={{ margin: '0 0 0 10px' }} size="small">
									<AddIcon fontSize="inherit" />
								</IconButton>
							</InputAdornment>
						)
					}
					fullWidth
				>
					{multiple ? null : (
						<MenuItem key="__" value="">
							-
						</MenuItem>
					)}
					{Array.isArray(options) &&
						options.map((option) => (
							<MenuItem key={option.LookupId} value={option.LookupId}>
								{strings[option.DisplayValue.replace(/\s+/g, '')] || options.DisplayValue}
							</MenuItem>
						))}
				</Select>
			);
		}
	);
	autocomplete = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			readOnly,
			disabled,
			required,
			onChange,
			setFieldValue,
			error
		}) => {
			const lookup = column.formLookup ? column.formLookup : column.lookup;
			const [{ strings }] = usePrerequisites();
			const options = useMemo(() => {
				let opts =
					typeof lookup === 'string'
						? typeof activeRecord?.combos === 'object'
							? activeRecord?.combos[lookup]
							: []
						: lookup;

				if (!Array.isArray(opts) && typeof opts !== 'function') {
					opts = [];
				}
				if (typeof lookup === 'string' || Array.isArray(lookup)) {
					opts = opts.map(({ LookupId, DisplayValue, ...rest }) => {
						const label = DisplayValue ? strings[DisplayValue.replace(/\s+/g, '')] ?? DisplayValue : LookupId;
						return {
							id: LookupId,
							label,
							...rest
						};
					});
				}
				return opts;
			}, [lookup, activeRecord]);

			const optionsArgs = [];
			if (Array.isArray(column.dependsOn)) {
				const dependsOnValues = {};
				for (const dependsOn of column.dependsOn) {
					dependsOnValues[dependsOn] = formik.values[dependsOn];
				}
				optionsArgs.push(dependsOnValues);
			}
			const valueFormatter = useCallback(
				(newValue) => {
					if (typeof column.autocompleteValueFormatter === 'function') {
						return column.autocompleteValueFormatter(newValue, { formik, activeRecord });
					}
					let val = !!newValue && typeof newValue === 'object' && 'id' in newValue ? newValue.id : newValue;
					if (Array.isArray(newValue)) {
						val = newValue.map((entry) => (typeof entry === 'object' && 'id' in entry ? entry.id : entry));
					}
					return val;
				},
				[formik.values]
			);

			const handleOnChange = useCallback(
				(event, newValue) => {
					onChange(newValue, false, true);
				},
				[field]
			);

			const handleBlur = useCallback(
				(event) => {
					event.persist();
					setTimeout(() => formik.setFieldTouched(field, true), 50);
				},
				[field]
			);

			return (
				<AsyncAutocomplete
					size="small"
					margin="dense"
					variant="outlined"
					{...otherProps}
					sx={{
						...otherProps?.sx,
						flex: 1
					}}
					label={inlineLabel ? '' : fieldLabel}
					name={field}
					required={required}
					multiple={column.multiple}
					readOnly={readOnly}
					disabled={disabled || readOnly}
					placeholder={column.placeholder || fieldLabel}
					value={formik.values[field]}
					valueParser={column.autocompleteValueParser}
					valueFormatter={valueFormatter}
					filterSelectedOptions={column.filterSelectedOptions}
					onCreateOption={column.onCreateOption}
					autoComplete={column.autoComplete}
					autoHighlight={column.autoHighlight}
					optionsArgs={optionsArgs}
					context={{ formik, activeRecord }}
					onChange={handleOnChange}
					onClose={column.onClose}
					onInputChange={column.onInputChange}
					disableClearable={column.disableClearable}
					getOptionDisabled={column.getOptionDisabled}
					onOpen={column.onOpen}
					filterMode={column.filterMode || 'client'}
					freeSolo={column.freeSolo}
					error={Boolean(error)}
					helperText={error || column.helperText}
					options={options}
					fullWidth={'fullWidth' in column ? column.fullWidth : true}
					{...column.inputProps}
					textFieldProps={{
						...column.inputProps?.textFieldProps,
						InputProps: {
							...otherProps?.textFieldProps?.InputProps,
							...column.inputProps?.textFieldProps?.InputProps
						},
						inputProps: {
							onBlur: column.onBlur || handleBlur,
							...otherProps?.textFieldProps?.inputProps,
							...column.inputProps?.textFieldProps?.inputProps
						},
						...otherProps?.textFieldProps
					}}
				/>
			);
		}
	);
	text = ({
		column,
		field,
		fieldLabel,
		formik = {},
		type = 'text',
		otherProps = {},
		inlineLabel = true,
		readOnly,
		disabled,
		required,
		setFieldValue,
		activeRecord,
		error
	}) => {
		const inputRef = useRef();
		const ref = useRef();
		const value = type === 'text' && (!(field in (formik?.values || {})) || !formik?.values[field]) ? '' : formik?.values[field];
		const hasFocusRef = useRef(false);
		const inputProps = { ...otherProps.inputProps };
		const [{ strings }] = usePrerequisites();
		if (readOnly || disabled) {
			inputProps.tabIndex = -1;
		}
		const placeholder = useMemo(() => {
			let placeholderVal = fieldLabel;
			if (column?.placeholder) {
				placeholderVal = strings[column.placeholder] || column.placeholder;
			}
			return placeholderVal;
		}, [column?.placeholder, fieldLabel]);

		const handleChange = useCallback(
			(event) => {
				event.persist();
				let val = event?.target?.value;
				if (type === 'number') {
					val = val = event?.target?.valueAsNumber;
					if (isNaN(val)) {
						val = value;
					}
				}
				if (!!error && !column.eager) {
					formik.setFieldTouched(field, false); // This will clear validation error on change
				}
				if (!hasFocusRef.current || column.eager) {
					setFieldValue(field, val, false, hasFocusRef.current);
				}
			},
			[error, value]
		);

		const handleFocus = () => {
			hasFocusRef.current = true;
		};

		const handleBlur = useCallback(
			async (event) => {
				event.persist();
				let val = event?.target?.value;
				if (type === 'number') {
					val = val = event?.target?.valueAsNumber;
					if (isNaN(val)) {
						val = undefined;
					}
				}
				hasFocusRef.current = false;
				const valueNeedsTrim = type === 'text' && typeof val === 'string' && /(^\s)|(\s$)/.test(val);
				val = valueNeedsTrim ? val.trim() : val;
				if (valueNeedsTrim || !column.eager) {
					if(inputRef.current) {
						inputRef.current.value = val;
					}
					setFieldValue(field, val, false);
					setTimeout(() => formik.setFieldTouched(field, true), 50); //Delay validation
				}
				if (typeof column.onBlur === 'function') {
					column.onBlur(event, { formik, value: val, activeRecord });
				}
			},
			[field]
		);

		const setValue = (val) => {
			if (inputRef.current && inputRef.current.value !== val) {
				inputRef.current.value = val; //Provision for resetting value
			}
		};

		useUniqueEffect(() => {
			setValue(value);
		}, [value]);

		// Extract disableClearable and remove it from otherProps
		const { disableClearable, ...restOtherProps } = otherProps;

		return (
			<TextField
				type={type || 'text'}
				size="small"
				margin="dense"
				variant="outlined"
				label={inlineLabel ? '' : fieldLabel}
				placeholder={placeholder}
				required={inlineLabel ? false : required}
				multiline={column.multiline}
				rows={column.multiline ? (column.rows > 0 ? column.rows : 4) : undefined}
				fullWidth
				name={field}
				defaultValue={value}
				disabled={disabled || readOnly}
				onChange={handleChange}
				onBlur={handleBlur}
				onFocus={handleFocus}
				helperText={error || column.helperText}
				error={Boolean(error)}
				inputRef={inputRef}
				autoComplete="off"
				ref={ref}
				{...(disableClearable !== undefined && { disableclearable: disableClearable })}
				{...restOtherProps}
				InputLabelProps={value?.length > 0 ? { shrink: true } : {}}
				{...column.inputProps}
				inputprops={{
					autoComplete: 'new-password',
					form: {
						autoComplete: 'off'
					},
					...inputProps,
					...column.inputProps?.inputProps
				}}
			/>
		);
	};
	string = withInputWrapper((props) => this.text(props));

	number = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			setFieldValue,
			disabled,
			readOnly,
			onChange,
			error
		}) => {
			const { minValue: min, maxValue: max } = column;
			const inputProps = { ...column.inputProps, min, max };
			const finalOtherProps = {
				...otherProps,
				inputProps
			};
			return this.text({
				column,
				field,
				fieldLabel,
				formik,
				activeRecord,
				type: 'number',
				otherProps: finalOtherProps,
				inlineLabel,
				setFieldValue,
				disabled,
				readOnly,
				onChange,
				error
			});
		}
	);
	password = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			disabled,
			readOnly,
			...rest
		}) => {
			const [showPassword, setShowPassword] = React.useState(false);
			const handleClickShowPassword = () => setShowPassword((show) => !show);

			const handleMouseDownPassword = (event) => {
				event.preventDefault();
			};
			return this.text({
				column,
				field,
				fieldLabel,
				formik,
				type: showPassword ? 'text' : 'password',
				disabled,
				readOnly,
				otherProps: {
					InputProps: {
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleClickShowPassword}
									onMouseDown={handleMouseDownPassword}
									disabled={disabled || readOnly}
									edge="end"
								>
									{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
								</IconButton>
							</InputAdornment>
						)
					},
					...otherProps
				},
				inlineLabel,
				activeRecord,
				...rest
			});
		}
	);
	date = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			setFieldValue,
			activeRecord,
			otherProps,
			inlineLabel = true,
			disabled,
			readOnly,
			required,
			error,
			maxDate,
			minDate
		}) => {
			const format = column.formFormat || util.getDateTimeFormat(false).date;
			const value = useMemo(() => {
				let val =
					typeof formik.values[field] === 'string' || !!formik.values[field]
						? dayjs(formik.values[field])
						: formik.values[field];
				if (typeof val === 'object' && val?.isValid()) {
					val =
						'isFormLocal' in column
							? column.isFormLocal
								? val.local(true)
								: val
							: column.isLocal
								? val.local(true)
								: val;
				} else {
					val = null;
				}
				return val;
			}, [formik.values[field]]);

			const handleOnChange = (newValue) => {
				let val;
				if (dayjs(newValue).isValid()) {
					val = newValue;
				}

				setFieldValue(field, val);
			};
			return (
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DesktopDatePicker
						readOnly={column.readOnly}
						disabled={disabled}
						fullWidth
						name={field}
						label={inlineLabel ? '' : fieldLabel}
						value={value}
						maxDate={maxDate}
						minDate={minDate}
						onBlur={formik.handleBlur}
						onChange={handleOnChange}
						format={format}
						onClose={() => setTimeout(() => formik.setFieldTouched(field, true), 100)}
						required={required}
						error={!!error}
						{...otherProps}
						slotProps={{
							...otherProps?.slotProps,
							textField: {
								variant: 'outlined',
								margin: 'dense',
								size: 'small',
								fullWidth: true,
								required: required,
								disabled: readOnly,
								error: !!error,
								helperText: error || column.helperText,
								placeholder: column.placeholder || fieldLabel,
								inputProps: {
									...otherProps?.slotProps?.textField?.inputProps,
									onBlur: () => formik.setFieldTouched(field, true)
								},
								...otherProps?.slotProps?.textField
							}
						}}
						localeText={{ fieldMonthPlaceholder: (params) => params.contentType === 'digit' ? 'MM' : 'MMM'}} // To override the default mui month placeholder
					/>
				</LocalizationProvider>
			);
		}
	);
	dateTime = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			readOnly,
			disabled,
			error,
			maxDate,
			setFieldValue,
			minDate,
			minTime,
			maxTime,
			timeFormat
		}) => {
			const [open, setOpen] = useState(false);
			const format = column.formFormat || column.format || util.getDateTimeFormat(column.showSecond).datetime;
			const value = useMemo(() => {
				let val =
					typeof formik.values[field] === 'string' || !!formik.values[field]
						? dayjs(formik.values[field])
						: formik.values[field]
							? formik.values[field]
							: null;
				if (typeof val === 'object' && val?.isValid()) {
					const isLocal = 'isFormLocal' in column ? column.isFormLocal : column.isLocal;
					val = isLocal ? val.local() : val;
				} else {
					val = null;
				}

				return val;
			}, [formik.values[field]]);

			const handleOnChange = (newValue) => {
				let val = null;
				if (dayjs(newValue).isValid()) {
					val = newValue;
				}
				setFieldValue(field, val);
			};

			const handleAccept = () => {
				// Close the picker when accept is clicked
				setOpen(false);
			};

			return (
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DateTimePicker
						name={field}
						label={inlineLabel ? '' : fieldLabel}
						value={value}
						open={open}
						onOpen={() => setOpen(true)}
						onClose={() => {
							setOpen(false);
							setTimeout(() => formik.setFieldTouched(field, true), 100);
						}}
						onBlur={formik.handleBlur}
						error={formik.touched[field] && formik.errors[field]}
						onChange={handleOnChange}
						format={format}
						maxDate={maxDate}
						minDate={minDate}
						ampm={!timeFormat && !minTime && !maxTime}
						minTime={minTime}
						maxTime={maxTime}
						slots={{ actionBar: DateTimePickerActionBar }}
						disableIgnoringDatePartForTimeValidation
						{...otherProps}
						{...column?.inputProps}
						slotProps={{
							...otherProps?.slotProps,
							...column?.inputProps?.slotProps,
							actionBar: {
								actions: ['accept'],
								onAccept: handleAccept
							},
							textField: {
								variant: 'outlined',
								margin: 'dense',
								size: 'small',
								fullWidth: true,
								error: !!error,
								helperText: error || column.helperText,
								placeholder: column.placeholder || fieldLabel,
								inputProps: {
									...otherProps?.slotProps?.textField?.inputProps,
									onBlur: () => formik.setFieldTouched(field, true)
								},
								...otherProps?.slotProps?.textField,
								...column?.inputProps?.slotProps?.textField
							}
						}}
						readOnly={readOnly}
						disabled={disabled}
						views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
						localeText={{ fieldMonthPlaceholder: (params) => params.contentType === 'digit' ? 'MM' : 'MMM'}} // To override the default mui month placeholder
					/>
				</LocalizationProvider>
			);
		}
	);
	time = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			validationSchema,
			readOnly,
			required,
			error,
			maxTime,
			minTime,
			setFieldValue
		}) => {
			const ampm = util.checkConfigData('TimeFormatAccess') === consts.TimeFormat.StandardFormat;
			const format =
				column.formFormat || column.format || ampm
					? column.showSecond
						? 'hh:mm:ss A'
						: 'hh:mm A'
					: column.showSecond
						? 'HH:mm:ss'
						: 'HH:mm';
			const value = useMemo(() => {
				let val =
					typeof formik.values[field] === 'string' || !!formik.values[field]
						? dayjs(formik.values[field])
						: formik.values[field] || null;
				if (typeof val === 'object' && val?.isValid()) {
					val =
						'isFormLocal' in column
							? column.isFormLocal
								? val.local()
								: val
							: column.isLocal
								? val.local()
								: val;
				} else {
					val = null;
				}
				return val;
			}, [formik.values[field]]);

			const handleOnChange = (newValue) => {
				let val;
				if (dayjs(newValue).isValid()) {
					val = newValue;
				}
				setFieldValue(field, val);
			};

			return (
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<TimePicker
						readOnly={readOnly}
						fullWidth
						name={field}
						label={inlineLabel ? '' : fieldLabel}
						value={value}
						maxTime={maxTime}
						minTime={minTime}
						error={error}
						format={format}
						ampm={false}
						ampmInClock={false}
						onChange={handleOnChange}
						onClose={() => setTimeout(() => formik.setFieldTouched(field, true), 100)}
						skipDisabled
						timeSteps={{ hours: 1, minutes: 1, seconds: 30 }}
						closeOnSelect
						{...otherProps}
						{...column.inputProps}
						slotProps={{
							...otherProps?.slotProps,
							...column.inputProps?.slotProps,
							actionBar: {
								actions: ['accept']
							},
							textField: {
								variant: 'outlined',
								margin: 'dense',
								size: 'small',
								fullWidth: true,
								required: required,
								disabled: readOnly,
								error: !!error,
								helperText: error || column.helperText,
								placeholder: column.placeholder || fieldLabel,
								inputProps: {
									...otherProps?.slotProps?.textField?.inputProps,
									onBlur: () => formik.setFieldTouched(field, true)
								},
								...otherProps?.slotProps?.textField,
								...column.inputProps?.slotProps?.textField
							},
							DigitalClockSectionItem: {
								defaultValue: dayjs()
							}
						}}
					/>
				</LocalizationProvider>
			);
		}
	);
	radio = withInputWrapper(
		({
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps = {},
			inlineLabel = true,
			readOnly,
			required,
			onChange,
			error
		}) => {
			const options =
				typeof column.options === 'function'
					? column.options({ formik, activeRecord })
					: Array.isArray(column.options)
						? column.options
						: [];
			const helperText = error || column.helperText;
			return (
				<FormControl component={Grid} container error className="inputWrapper" variant="standard">
					<Box>
						{!inlineLabel && <FormLabel required={required}>{fieldLabel}</FormLabel>}
						<RadioGroup name={field} value={formik.values[field]} onChange={onChange} {...otherProps}>
							{Array.isArray(options) &&
								options.map((option, i) => (
									<FormControlLabel
										value={option.value}
										control={<Radio />}
										label={option.label}
										disabled={readOnly}
										key={`${field}-option-${i}`}
									/>
								))}
						</RadioGroup>
						{(helperText || error) && <FormHelperText error={error}>{error || helperText}</FormHelperText>}
					</Box>
				</FormControl>
			);
		}
	);

	custom = ({
		Input,
		inputProps: {
			column,
			field,
			fieldLabel,
			formik,
			activeRecord,
			otherProps,
			inlineLabel = true,
			validationSchema,
			readOnly,
			required,
			error,

			...rest
		}
	}) => {
		const CustomInput = column?.customizeInput;
		const columnVal = { ...column, required, readOnly };
		delete columnVal.customizeInput; // Avoid Cyclic object
		return (
			<CustomInput
				Input={Input}
				inputProps={{
					column: columnVal,
					field,
					fieldLabel,
					formik,
					activeRecord,
					otherProps,
					inlineLabel,
					validationSchema,
					error,
					readOnly,
					...rest
				}}
			/>
		);
	};
}

const fieldMappers = new FieldMappers();
fieldMappers.bool = fieldMappers.boolean;
fieldMappers.int = fieldMappers.number;

const evalSubmissionDeps = ({ location, formik, activeRecord, modelConfig, isReadOnly, isNew }) => {
	const readOnly =
		formik?.status?.readOnly ||
		Boolean(
			isReadOnly
				? isReadOnly
				: typeof modelConfig.readOnly === 'function'
					? modelConfig.readOnly({ activeRecord, formik, location })
					: modelConfig.readOnly
		);
	const disableAdd = Boolean(
		isNew && typeof modelConfig.disableAdd === 'function'
			? modelConfig.disableAdd({ activeRecord, formik, location })
			: modelConfig.disableAdd
	);

	return { readOnly, disableAdd };
};

const formContainerSx = {
	'& .MuiFormLabel-root:not(.MuiTypography-root)': {
		color: 'primary.light',
		fontWeight: 600,
		fontSize: '0.95rem',
		display: 'table',
		whiteSpace: 'pre-wrap' /* css-3 */,
		wordWrap: 'break-word' /* Internet Explorer 5.5+ */,
		msWordBreak: 'break-all',
		wordBreak: 'break-word',
		fallbacks: [
			{
				wordBreak: 'break-all'
			}
		],
		webkitHyphens: 'auto',
		mozHyphens: 'auto',
		msHyphens: 'auto',
		hyphens: 'auto',
		marginRight: 0.5,
		'& .MuiFormLabel-asterisk': {
			color: 'error.light'
		}
	},
	'& .inputWrapper': {
		padding: (theme) => theme.spacing(1, 0),
		height: 'fit-content',
		'& label:not(.MuiTypography-root)': {
			fontWeight: 'bold'
		},
		'& .MuiFormControlLabel-label:not(.MuiTypography-root)': {
			fontWeight: 'bold'
		},
		'& .Mui-disabled:not(.MuiAutocomplete-input)': {
			backgroundColor: 'action.hover',
			color: 'action.disabledBackground',
			'& .MuiChip-root': {
				color: 'text.primary'
			}
		},
		'& .MuiAutocomplete-inputRoot': {
			'&.Mui-disabled': {
				backgroundColor: 'action.selected',
				color: 'action.disabledBackground',
				WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
				'&.MuiAutocomplete-input': {
					backgroundColor: 'transparent'
				}
			}
		},
		'&.hiddenField': {
			display: 'none !important'
		},
		'& .MuiFormControlLabel-root': {
			width: 'fit-content'
		}
	},
	'& .MuiCardHeader-action': {
		margin: 0,
		alignSelf: 'center'
	},
	'& .MuiFormControl-root-MuiTextField-root': {
		marginTop: (theme) => theme.spacing(0.4)
	},
	'& .formGroupInputs': {
		p: 0
	},
	'& .MuiCardActions-root': { padding: (theme) => theme.spacing(1, 2) },
	'& .MuiGrid-root:empty': { display: 'none' }
};

const FormDialog = forwardEnsuredRef(
	(
		{
			modelConfig,
			activeRecord,
			setActiveRecord,
			parent,
			initialValues: initialValuesProp,
			isReadOnly = false,
			hideCancel = false,
			hideSubmit = false,
			loading,
			onSubmit,
			containerProps,
			saveBtnText = 'Save',
			cancelBtnText = 'Cancel',
			isNew,
			formActions = [],
			formActionsPosition = 'left',
			onRecordSaved,
			actionsContainerProps = {},
			submitBtnProps,
			cancelBtnProps,
			disableAlerts
		},
		ref
	) => {
		const theme = useTheme();
		const navigate = useNavigate();
		const [{ strings }] = usePrerequisites();
		const innerRef = useRef();
		const location = useLocation();
		const locationRef = useLiveRef(location);
		const [isModalOpen, setIsModalOpen] = useState(false);
		const [searchParams, setSearchParams] = useSearchParams();
		const searchParamsRef = useLiveRef(searchParams);
		const licenseData = util.localData?.LicenseAppName;
		const isCatViewer = licenseData?.includes('catviewer');
		const { hideTopHeader, enableFormPrint, enableFormHelp, additionalButtons, FormExtension, formGroupsComponent } =
			modelConfig;
		const [showConfirm, setShowConfirm] = useState(false);
		const [isLoading, setIsLoading] = useState(false);

		const FormGroupsComponent = useMemo(
			() =>
				typeof formGroupsComponent === 'function'
					? formGroupsComponent
					: typeof formGroupsComponent === 'string' && formGroupsComponents.has(formGroupsComponent.toLowerCase())
						? formGroupsComponents.get(formGroupsComponent.toLowerCase())
						: formGroupsComponents.get('sections'),
			[]
		);
		const title = useMemo(() => {
			const titleStr = modelConfig.formTitleField || modelConfig.title;
			let titleVal = titleStr && titleStr in strings ? strings[titleStr] : titleStr;
			if (!modelConfig?.title && !!modelConfig?.name) {
				titleVal = modelConfig.name in strings ? strings[modelConfig.name] : modelConfig.name;
			}
			return titleVal;
		}, [modelConfig.formTitleField, modelConfig.title, modelConfig.name]);

		const validationSchema = useMemo(
			() => modelConfig.getValidationSchema({ activeRecord }, ref),
			[activeRecord, modelConfig]
		);
		const initialValues = useMemo(() => {
			const initialVals = !isNew
				? { ...modelConfig.initialValues, ...activeRecord?.record, ...initialValuesProp }
				: { ...activeRecord?.record, ...modelConfig.initialValues, ...initialValuesProp };

			for (const column of modelConfig.formDef) {
				if (activeRecord?.record && Array.isArray(activeRecord?.record[column.field]) && (activeRecord?.record[column.field]?.length !== initialVals[column.field]?.length)) {
					initialVals[column.field] = activeRecord?.record[column.field].length > initialVals[column.field]?.length? activeRecord.record[column.field] :  initialVals[column.field];
				}
				if (column.isNotformfield) {
					delete initialVals[column.field];
				}
				if(column.type === 'file') {
					initialVals[column.field] = activeRecord?.record[column.field] || '';
				}
				if (
					['date', 'time', 'datetime'].includes(column.type?.toLowerCase()) &&
					column.name in initialVals &&
					!!initialVals[column.name]
				) {
					let dateVal = dayjs(
						typeof initialVals[column.name] === 'string'
							? initialVals[column.name].replace('$D{', '').replace('}', '')
							: initialVals[column.name]
					);
					if (dateVal.isValid()) {
						initialVals[column.name] = column.isLocal || column.isFormLocal ? dateVal.local() : dateVal;
					}
				} else if (Array.isArray(column.options) && column?.name in initialVals) {
					const initialVal = initialVals[column.name];
					const valueInOptions = column.options.some((option) => option.value === initialVal);
					if (!valueInOptions) {
						initialVals[column.name] = '';
					}
				}
			}
			return initialVals;
		}, [activeRecord?.record, initialValuesProp]);

		const handleClose = useCallback(
			(opts = { refresh: true }) => {
				const returnTo = decodeURIComponent(searchParamsRef.current.get('returnTo') || '');
				if (returnTo) {
					navigate(returnTo);
				}
				if (typeof setActiveRecord === 'function') {
					setActiveRecord(undefined, { ...opts, searchParamsRef });
				}
			},
			[setActiveRecord]
		);

		const handleOnFormikSubmit = useCallback(
			async (values, formik) => {
				const { resetForm } = formik;
				let toSave = {};
				Object.keys(values).forEach((key) => {
					const formField = modelConfig.formDef.find((f) => f.formField === key || f.field === key);
					if (formField && !formField?.excludeOnSubmit) {
						let v = values[key];
						if (formField.multiple && Array.isArray(v) && !formField.preserveTypeOnSubmit) {
							v = JSON.stringify(v);
						} else if (
							(Array.isArray(v) || v?.constructor === {}.constructor) &&
							!formField.preserveTypeOnSubmit
						) {
							// If array or JSON object
							v = JSON.stringify(v);
						} else if (formField.type === 'date' && typeof v === 'string' && v.startsWith('$')) {
							v = util.fixDates(v);
						} else if (['date', 'dateTime', 'time'].includes(formField.type) && !!v) {
							if (typeof v === 'string') {
								v = util.fixDates(v);
							}
							if (typeof formField.formFormat === 'string' || typeof formField.format === 'string') {
								let dayjsV = dayjs(v);
								if (dayjsV.isValid()) {
									v = dayjsV.format(formField.formFormat || formField.format);
								}
							}
						}

						toSave[key] = v;
					}
				});

				// Check if the record is new (...without lookupTypeId)
				if (typeof modelConfig.customizeSaveData === 'function') {
					try {
						const toSaveCustomizations = await modelConfig.customizeSaveData(toSave, {
							activeRecord,
							values,
							resetForm,
							searchParams
						});
						toSave = { ...toSave, ...toSaveCustomizations };
					} catch (error) {
						console.error('customizeSaveData error', error);
					}
				}
				let submitRes;
				if (typeof onSubmit === 'function') {
					submitRes = await onSubmit(toSave, { ...modelConfig, ...formik, setActiveRecord, activeRecord, searchParams });
				} else {
					submitRes = await saveRecord({
						api: modelConfig.endPoint,
						id: activeRecord?.id,
						values: toSave,
						action: modelConfig.action || 'save',
						parseOnSaveError: modelConfig.parseOnSaveError,
						disableAlerts,
						onSuccess: (res) => {
							if (typeof onRecordSaved === 'function') {
								onRecordSaved(res, { data: toSave, formik, searchParams, setSearchParams  });
							}
							if (typeof modelConfig.onRecordSaved === 'function') {
								modelConfig.onRecordSaved(res, { data: toSave, formik, searchParams, setSearchParams });
							}
							handleClose({ refresh: true });
						}
					})
						.then((res) => {
							if (res) {
								if (typeof onRecordSaved === 'function') {
									onRecordSaved(res, { data: toSave, formik });
								}
								if (typeof modelConfig.onRecordSaved === 'function') {
									modelConfig.onRecordSaved(res, { data: toSave, formik, searchParams, setSearchParams });
								}
								handleClose({ refresh: true });
							}
						})
						.catch((err) => {
							//Do nothing
						});
				}
				setIsLoading(false);
				return submitRes;
			},
			[modelConfig.formDef, modelConfig.endPoint, onSubmit, activeRecord?.id, onRecordSaved, disableAlerts]
		);

		const formik = useFormik({
			enableReinitialize: true,
			initialValues: initialValues,
			validationSchema: validationSchema,
			onSubmit: handleOnFormikSubmit
		});
		const { readOnly, disableAdd } = evalSubmissionDeps({
			location,
			formik,
			activeRecord,
			modelConfig,
			isReadOnly,
			isNew
		});

		const handleDeleteCancel = () => {
			setShowConfirm(false);
		};

		const handleDeleteConfirm = () => {
			setShowConfirm(false);
			setIsLoading(true);
			deleteRecord({ id: activeRecord.id, api: modelConfig.api })
				.then((success) => {
					if (success) {
						handleClose({ refresh: true });
					}
				})
				.finally(() => setIsLoading(false));
		};

		let autoFocusSet = false;

		const openHelp = () => {
			setIsModalOpen(true)
		};

		const getFormField = useCallback(
			({ column, fieldProps = {} }) => {
				if (
					(parent && column.lookup && parent === column.lookup) ||
					column.isNotformfield ||
					(column.omitOnNew && isNew)
				) {
					return;
				}
				const field = column.formField || column.field;
				let fieldLabel = column.label || column.header || field;
				const otherProps = { defaultValue: column.defaultValue };
				let inlineLabel = 'inlineLabel' in fieldProps ? column.fieldProps : true;
				if ('inlineLabel' in column) {
					inlineLabel = Boolean(column.inlineLabel);
				}
				const formFieldProps = { ...fieldProps, inlineLabel };
				if (!autoFocusSet && column.readOnly !== true) {
					otherProps.autoFocus = true;
					autoFocusSet = true;
				}
				let fieldType = column.type;
				if (!column.type) {
					fieldType = 'string';
				}
				if ((column.formLookup || column.lookup) && column.type !== 'select') {
					fieldType = 'autocomplete';
				} else if (column.options) {
					fieldType = 'radio';
				}
				let formField = {
					CustomizedInput: column.customizeInput ? fieldMappers.custom : null,
					Input: fieldMappers[fieldType],
					column: {
						...formFieldProps,
						...column,
						readOnly: isReadOnly ? isReadOnly : column.readOnly,
						inlineLabel
					},
					field,
					fieldLabel: strings[fieldLabel] || fieldLabel,
					...formFieldProps,
					otherProps: {
						...otherProps,
						...formFieldProps.otherProps
					},
					fieldType
				};
				if (!formField.CustomizedInput) {
					delete formField.CustomizedInput;
				}
				return formField;
			},
			[isNew, isReadOnly]
		);

		const formGroups = useMemo(() => {
			let formGroupsArr = Array.isArray(modelConfig.formGroups) ? modelConfig.formGroups : [];
			if (Array.isArray(modelConfig?.formGroups)) {
				formGroupsArr = modelConfig.formGroups.reduce((acc, curr) => {
					const fields = typeof curr.fields === 'function' ? curr.fields({ formik, activeRecord }) : curr.fields;
					const formFieldsElements = fields
						.map((fieldName) => {
							let formFieldElement = null;
							const fieldColumn = modelConfig.formDef
								.filter((column) => !column.isNotformfield)
								.find((column) =>
									'formField' in column ? column.formField === fieldName : column.field === fieldName
								);
							if (typeof fieldColumn === 'object') {
								formFieldElement = getFormField({
									column: fieldColumn,
									fieldProps: { ...curr?.fieldProps }
								});
							}
							return formFieldElement;
						})
						.filter((element) => !!element);

					acc.push({ ...curr, inputs: formFieldsElements });
					return acc;
				}, []);
			} else {
				formGroupsArr = [
					{
						inputs: modelConfig.formDef.map((column) => getFormField({ column })).filter((element) => !!element)
					}
				];
			}
			return formGroupsArr.filter((formGroup) => {
				const isFormGroupVisible =
					typeof formGroup.visible === 'function'
						? formGroup.visible({ activeRecord, location: locationRef.current })
						: formGroup.visible ?? true;
				return isFormGroupVisible;
			});
		}, [modelConfig.formDef, activeRecord]);
		const firstMountRef = useRef(true);
		const formikRef = useLiveRef(formik);
		const getFormik = useCallback(() => formikRef.current, [formikRef]);
		const openFormGroupIndex = useMemo(() => {
			let openIndexes = [0];

			if (searchParams.has('form-group')) {
				const currentFormGroupParam = searchParamsRef.current.has('form-group')
					? decodeURIComponent(searchParamsRef.current.get('form-group'))
					: '';
				const openFormGroupsNames = currentFormGroupParam?.trim?.().split?.(',') ?? [];
				if (openFormGroupsNames.length > 0) {
					openIndexes = openFormGroupsNames
						.map((name) => {
							const decodedName = name.toLowerCase();
							return formGroups.findIndex((formGroup) => {
								const formGroupName = formGroup?.name || formGroup?.header || formGroup?.alternateHeader;
								const visible =
									typeof formGroup.visible === 'function'
										? formGroup.visible({ activeRecord, location: locationRef.current })
										: formGroup.visible ?? true;

								return visible && formGroupName && decodedName === formGroupName?.toLowerCase?.();
							});
						})
						.filter((i) => i >= 0);
				}
			}

			return formGroupsComponent === 'accordions' ? openIndexes : openIndexes[0];
		}, [searchParams, formGroupsComponent, formGroups]);

		useDidMount(() => {
			firstMountRef.current = false;
		});

		const setOpenGroupSearchParam = useCallback(
			(toggle, preserveExisting = false) => {
				const searchParamsObj = {};
				const currentFormGroupParam = searchParamsRef.current.has('form-group')
					? decodeURIComponent(searchParamsRef.current.get('form-group'))
					: '';
				let openFormGroupsArr = (currentFormGroupParam?.split?.(',') ?? [])
					.filter((name) => !!name?.trim?.())
					.map((name) => name.toLowerCase());
				for (const [key, value] of searchParamsRef.current.entries()) {
					searchParamsObj[key] = value;
				}
				const toggleIndex = (acc, currindex) => {
					const formGroup = Array.isArray(formGroups) ? formGroups[currindex] : null;
					let formGroupName = formGroup?.name || formGroup?.header || formGroup?.alternateHeader;
					const formGroupNameIndex = acc.indexOf(formGroupName?.toLowerCase());

					if (formGroupName) {
						const visible =
							typeof formGroup.visible === 'function'
								? formGroup.visible({ activeRecord, location: locationRef.current })
								: formGroup.visible ?? true;
						formGroupName = encodeURIComponent(formGroupName?.toLowerCase?.());
						if (formGroupNameIndex === -1) {
							if (formGroupsComponent === 'accordions') {
								acc.push(formGroupName);
							} else {
								acc = [formGroupName];
							}
						} else if (!preserveExisting || !visible) {
							acc.splice(formGroupNameIndex, 1);
						}
					}
					return acc;
				};
				openFormGroupsArr = (Array.isArray(toggle) ? toggle : [toggle]).reduce(toggleIndex, openFormGroupsArr);

				if (openFormGroupsArr.length === 0) {
					delete searchParamsObj['form-group'];
				} else {
					searchParamsObj['form-group'] = openFormGroupsArr.join(',');
				}

				setSearchParams(searchParamsObj);
			},
			[formGroups]
		);

		const setOpenFormGroupIndex = useCallback(
			(i, preserveExisting = false) => setOpenGroupSearchParam(i, preserveExisting),
			[setOpenGroupSearchParam]
		);

		const formSubmittable = !isLoading && isNew ? !disableAdd : !readOnly;
		const evalFieldValidationError = useCallback(
			({ field, message, touch = false, includeUntouched = false }) => {
				let msg;
				try {
					const column = modelConfig.formDef?.find((col) => field && col.name === field);
					if (touch && column?.name && !formikRef.current.touched[column?.name]) {
						formikRef.current.setFieldTouched(column.name, true);
					}
					const label = strings[column?.fieldLabelName] || strings[column?.label] || column?.label;
					msg = message;
					if (!!msg && typeof strings === 'object' && msg in strings) {
						msg = strings[msg]?.replaceAll('{0}', label);
					}

					msg = includeUntouched || touch || formikRef.current.touched[column?.name] ? msg : undefined;
					if (msg?.includes?.('{0}')) {
						msg = msg.replaceAll('{0}', label);
					}
				} catch (error) {
					console.error('evalFieldValidationError error', error);
				}

				return msg;
			},
			[modelConfig.formDef, strings]
		);

		const getValidationErrorsMsg = useCallback(async () => {
			if (typeof modelConfig.getValidationErrorsMsg === 'function') {
				return await modelConfig.getValidationErrorsMsg({
					formik: formikRef.current,
					activeRecord,
					validationSchema
				});
			}

			const [touchFields, message] = await validationSchema
				.validate(formikRef.current.values, { abortEarly: false })
				.then(function () {
					// Success
					return [{}, null];
				})
				.catch(function (err) {
					if (Array.isArray(err?.inner)) {
						return err.inner.reduce(
							(acc, e) => {
								let touch = acc[0];
								let msg = acc[1];
								const pathErrStr = evalFieldValidationError({
									field: e?.path,
									message: e?.message,
									touch: true,
									includeUntouched: true
								});
								if (pathErrStr) {
									if (!msg.includes(pathErrStr)) {
										msg += `${msg?.length > 0 ? ' </br> ' : ''}`;
										msg += `<small class="text-error">${strings[pathErrStr] || pathErrStr}</small>`;
									}

									touch[e?.path] = true;
								}
								return [touch, msg];
							},
							[{}, '']
						);
					}

					return [{}, ''];
				});

			if (message) {
				await formik.setTouched(touchFields);
			}
			return message;
		}, [activeRecord, formik.values]);

		const handleSubmit = useCallback(
			async (event) => {
				event?.persist?.();
				event?.preventDefault?.();
				const errorsFields = Object.keys(formikRef.current.errors);
				let msg;
				if (errorsFields.length > 0) {
					msg = await getValidationErrorsMsg();
				}
				if (msg) {
					Swal.fire({
						icon: 'error',
						title: strings.Error,
						html: `${strings.FormValidationError} <br /> ${msg}`,
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
					const touchedFields = formikRef.current.touched ?? {};
					const formGroupIndexes = [];
					if (formGroups?.length > 1) {
						let errorFieldName;
						for (let i = 0; i < errorsFields.length; i++) {
							const formGroupIndex = formGroups.findIndex(
								(formGroup) => Array.isArray(formGroup.fields) && formGroup.fields.includes(errorsFields[i])
							);
							errorFieldName = formGroupIndex >= 0 ? errorsFields[i] : errorFieldName;
							touchedFields[errorsFields[i]] = true;
							if (formGroupIndex > -1) {
								if (!formGroupIndexes.includes(formGroupIndex)) {
									formGroupIndexes.push(formGroupIndex);
								}
							}
						}
					}

					if (formGroupIndexes.length > 0) {
						formik.setFieldTouched(touchedFields);
						setOpenFormGroupIndex(formGroupIndexes, true);
					}
				} else if (formSubmittable) {
					formikRef.current.handleSubmit(event);
				}
			},
			[formSubmittable, setOpenFormGroupIndex, getValidationErrorsMsg, formGroups]
		);

		const getActiveRecord = useCallback(() => activeRecord, [activeRecord]);
		const getModelConfig = useCallback(() => modelConfig, [modelConfig]);
		const getInitialValues = useCallback(() => initialValues, [initialValues]);
		const getDOMElement = () => innerRef.current;
		const lodingIndicatorVisible = isLoading || formikRef.current.isSubmitting;

		const ready = useDeepMemo(
			() => !loading && typeof activeRecord?.record === 'object' && Object.entries(activeRecord?.record)?.length > 0,
			[activeRecord?.record, loading]
		);

		useUniqueEffect(() => {
			if (ready) {
				if (typeof modelConfig.onFormLoad === 'function') {
					setTimeout(
						() =>
							modelConfig.onFormLoad({
								activeRecord,
								formik: formikRef.current,
								location: locationRef.current,
								getFormik
							}),
						500
					); // use a timeout to allow for initialization
				}
			}
		}, [ready]);

		useImperativeHandle(
			ref,
			() => {
				return {
					submit: handleSubmit,
					getActiveRecord,
					getFormik,
					getModelConfig,
					getInitialValues,
					evalFieldValidationError,
					getValidationErrorsMsg,
					close: handleClose,
					getDOMElement,
					setOpenFormGroupIndex,
					setActiveRecord
				};
			},
			[
				handleSubmit,
				getActiveRecord,
				getFormik,
				getModelConfig,
				getInitialValues,
				evalFieldValidationError,
				getValidationErrorsMsg,
				handleClose,
				setOpenFormGroupIndex,
				setActiveRecord
			]
		);
		return (
			<ModelFormProvider
				value={{
					loading: lodingIndicatorVisible,
					formGroups,
					formik: formik,
					activeRecord,
					modelConfig,
					validationSchema,
					initialValues,
					isReadOnly,
					submit: handleSubmit,
					getActiveRecord,
					setActiveRecord,
					getFormik,
					getModelConfig,
					getInitialValues,
					evalFieldValidationError,
					getValidationErrorsMsg,
					close: handleClose,
					getDOMElement,
					setOpenFormGroupIndex,
					openFormGroupIndex
				}}
			>
				<Card
					component="form"
					{...containerProps}
					onSubmit={handleSubmit}
					sx={{ ...formContainerSx, ...containerProps?.sx }}
					ref={innerRef}
				>
					{!hideTopHeader ||
						(!enableFormHelp && !enableFormPrint && !additionalButtons?.length && (
							<CardHeader
								action={
									(enableFormHelp || enableFormPrint || additionalButtons?.length) && (
										<Stack direction="row" alignItems="center" sx={{ gap: theme.spacing() }}>
											{enableFormHelp && isCatViewer && (
												<>
													<IconButton
													type="button" //important as this is inside form
													color="primary"
													onClick={openHelp}
													title={strings.Help}
													className="print-hide"
													>
														<HelpIcon fontSize="inherit" />
													</IconButton>
													<HelpForm filename={title} scope={this} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
												</>
											)}
											{enableFormPrint && (
												<IconButton
													type="button"
													color="success"
													className="print-hide"
													onClick={printForm}
													title={strings.Print}
												>
													<PrintIcon fontSize="inherit" />
												</IconButton>
											)}
											{Array.isArray(additionalButtons) &&
												additionalButtons.map((button, index) => <Box key={index}>{button}</Box>)}
										</Stack>
									)
								}
							/>
						))}

					<CardContent sx={{ p: theme.spacing(1, 2) }}>
						<Grid container className="print-hide">
							<Grid size={12} item>
								<LoadingDialog open={lodingIndicatorVisible || loading} />
								<Confirm
									title={strings.Delete}
									body={strings.DeleteAlertText}
									open={showConfirm}
									onCancel={handleDeleteCancel}
									onConfirm={handleDeleteConfirm}
								/>
							</Grid>
						</Grid>
						{(lodingIndicatorVisible || loading) && !activeRecord && (
							<Grid container alignItems="center" justifyContent="center" className="print-hide">
								<Grid size={'auto'}>
									<CircularProgress size={24} />
								</Grid>
							</Grid>
						)}
						{!!activeRecord && (
							<Grid container>
								<Grid size={12}>
									<FormGroupsComponent />
								</Grid>

								{!!FormExtension && (
									<Grid size={12}>
										<FormExtension
											formik={formik}
											activeRecord={activeRecord}
											isNew={isNew}
											modelConfig={modelConfig}
										/>
									</Grid>
								)}
							</Grid>
						)}
					</CardContent>
					{!!activeRecord && !loading && (
						<CardActions {...actionsContainerProps}>
							{(formActionsPosition === 'right' || formActionsPosition === 'center') && (
								<Box sx={{ flex: 1 }} />
							)}
							{!hideSubmit && !modelConfig.hideSubmit && (
								<Button
									type="submit"
									variant="contained"
									color="success"
									disabled={!formSubmittable}
									startIcon={saveBtnText === 'Run' ? <LoopIcon /> : <SaveIcon />}
									disableElevation
									{...submitBtnProps}
								>
									{strings[saveBtnText] || saveBtnText}
								</Button>
							)}
							{!hideCancel && (
								<Button
									type="button"
									variant="contained"
									color="warning"
									onClick={() => handleClose({ refresh: false })}
									startIcon={<CloseIcon />}
									disableElevation
									{...cancelBtnProps}
								>
									{strings[cancelBtnText] || cancelBtnText}
								</Button>
							)}
							{Array.isArray(formActions) &&
								formActions.map(({ text, Component = Button, onClick, ...formAction }, i) => (
									<Component
										formik={formik}
										{...(typeof Component !== 'string' && { closeform: handleClose })}
										activerecord={activeRecord}
										onClick={(event) => {
											if (typeof onClick === 'function') {
												onClick({ event, formik, activeRecord });
											}
										}}
										{...formAction}
										key={`formAction-${i}`}
									>
										{text}
									</Component>
								))}
							{Array.isArray(modelConfig.formActions) &&
								modelConfig.formActions.map(({ text, Component = Button, onClick, ...formAction }, i) => (
									<Component
										formik={formik}
										activeRecord={activeRecord}
										onClick={(event) => {
											if (typeof onClick === 'function') {
												onClick({ event, formik, activeRecord, navigate });
											}
										}}
										{...formAction}
										closeForm={handleClose}
										key={`modelConfig-formAction-${i}`}
									>
										{text}
									</Component>
								))}
							{(formActionsPosition === 'left' || formActionsPosition === 'center') && (
								<Box sx={{ flex: 1 }} />
							)}
						</CardActions>
					)}
				</Card>
			</ModelFormProvider>
		);
	}
);

export default memo(FormDialog);
